import { defineStore } from 'pinia'
import { ref } from 'vue'
import Network from '@/Network'
import type { DoctorDetailResponse } from '@/Network/Types/Responses/CompanyResponses'
import type { DoctorCrmResponse } from '@/Network/Types/Responses/DoctorCrmResponses'
import type { AddDoctorCrmRequest } from '@/Network/Types/Requests/DoctorCrmRequests'
import { ServiceError, getApiErrorMessage } from '@/utils/apiError'

export const useDoctorDetailStore = defineStore('doctorDetail', () => {
  const doctorDetail = ref<DoctorDetailResponse | null>(null)
  const doctorCrms = ref<DoctorCrmResponse[]>([])
  const isLoading = ref(false)
  const isError = ref(false)
  const errorMessage = ref<string | null>(null)

  let currentRequestId = 0

  /**
   * Em modo `silent` nao zera `doctorDetail` nem liga `isLoading` — usado para
   * refresh apos acoes na propria pagina (ex.: reenvio de convite) sem piscar
   * o skeleton da tela inteira.
   */
  async function fetchDoctorById(doctorId: number, options?: { silent?: boolean }): Promise<void> {
    const silent = options?.silent ?? false
    const requestId = ++currentRequestId
    try {
      if (!silent) {
        isLoading.value = true
        isError.value = false
        errorMessage.value = null
        doctorDetail.value = null
        doctorCrms.value = []
      }
      // getDoctorCrms tem fallback silencioso ([]) — nunca derruba o detalhe
      const [result, crms] = await Promise.all([
        Network.company.getDoctorById(doctorId),
        Network.doctors.getDoctorCrms(doctorId)
      ])
      if (requestId !== currentRequestId) return
      doctorDetail.value = result
      doctorCrms.value = crms
    } catch (e) {
      if (requestId !== currentRequestId) return
      console.error(e)
      if (!silent) {
        isError.value = true
        errorMessage.value = getApiErrorMessage(e, 'Não foi possível carregar os dados do médico.')
        doctorDetail.value = null
        doctorCrms.value = []
      }
    } finally {
      if (requestId === currentRequestId && !silent) {
        isLoading.value = false
      }
    }
  }

  /**
   * Reenvia o convite do medico carregado para uma empresa. Nao ha endpoint de
   * reenvio dedicado: recria via POST /companies/invite-doctor com o CRM do
   * medico. Erros sao relancados — o toast fica no componente chamador.
   * As pré-condições locais lançam `ServiceError` para que a mensagem pt-BR
   * chegue ao toast (um `Error` cru cairia no fallback genérico).
   */
  async function resendInvite(companyId: number): Promise<void> {
    const detail = doctorDetail.value
    const crm = detail?.doctor.mainCrm
    if (!detail || !crm?.number || !crm?.uf) {
      throw new ServiceError(
        'Não foi possível identificar o CRM do médico para reenviar o convite.'
      )
    }
    await Network.company.inviteDoctorForCompany({
      crm: crm.number,
      crmUf: crm.uf,
      autoApprove: false,
      companyIds: [companyId]
    })
    await fetchDoctorById(detail.doctor.id, { silent: true })
  }

  /**
   * Transfere/adiciona um CRM ao médico carregado. A resposta já é a lista
   * atualizada de CRMs; o detalhe é recarregado em modo silent porque o
   * main_crm (e o nome exibido) podem ter mudado. Erros são relançados — o
   * toast fica no componente chamador.
   */
  async function addCrm(payload: AddDoctorCrmRequest): Promise<void> {
    const detail = doctorDetail.value
    if (!detail) {
      throw new ServiceError('Nenhum médico carregado para receber o CRM.')
    }
    const crms = await Network.doctors.addDoctorCrm(detail.doctor.id, payload)
    doctorCrms.value = crms
    await fetchDoctorById(detail.doctor.id, { silent: true })
  }

  function $reset() {
    doctorDetail.value = null
    doctorCrms.value = []
    isLoading.value = false
    isError.value = false
    errorMessage.value = null
  }

  return {
    doctorDetail,
    doctorCrms,
    isLoading,
    isError,
    errorMessage,
    fetchDoctorById,
    resendInvite,
    addCrm,
    $reset
  }
})
